import React, { useState, useMemo, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import T from '../styles/theme';
import {Avatar, Badge, Btn, Select, Checkbox } from '../components/UI';
import Icon from '../components/Icon';
import { useStore } from '../data/store';
import { useAuth } from '../data/auth';
import { useToast } from '../components/Toast';
import supabase from '../data/supabase';
import apiFetch from '../data/api';
import ComposeModal from '../components/ComposeModal';
import CalendarInviteCard from '../components/CalendarInviteCard';
import { stripHtmlTags, stripQuotedReply, makePreview } from '../lib/emailParsing';

const cfDateTime = (d) => { if (!d) return "—"; const dt = new Date(d); const m = String(dt.getMonth()+1).padStart(2,"0"); const dy = String(dt.getDate()).padStart(2,"0"); const y = String(dt.getFullYear()).slice(-2); let h = dt.getHours(); const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12; const mn = String(dt.getMinutes()).padStart(2,"0"); return m+"/"+dy+"/"+y+" "+h+":"+mn+" "+ap; };

/* ── Seed email data ── */
const EMAILS_INIT = [];

const TEMPLATES = [
  { id: 1, name: "Initial Outreach", subject: "Quick question about {{company}}", body: "Hi {{first_name}},\n\nI noticed {{company}} has been {{trigger_event}}. We help companies like yours {{value_prop}}.\n\nWould you be open to a 15-minute call this week?\n\nBest,\n{{sender_name}}" },
  { id: 2, name: "Post-Demo Follow-up", subject: "Great meeting today, {{first_name}}", body: "Hi {{first_name}},\n\nReally enjoyed our conversation today. As discussed, here are the next steps:\n\n{{next_steps}}\n\nI'll send over the proposal by {{date}}. In the meantime, feel free to reach out with any questions.\n\nBest,\n{{sender_name}}" },
  { id: 3, name: "Proposal Send", subject: "Proposal: {{company}} x CoreFlow", body: "Hi {{first_name}},\n\nAs promised, please find attached our proposal for {{company}}.\n\nKey highlights:\n- {{highlight_1}}\n- {{highlight_2}}\n- {{highlight_3}}\n\nHappy to walk through any questions on a call.\n\nBest,\n{{sender_name}}" },
  { id: 4, name: "Re-engagement", subject: "{{first_name}}, quick update", body: "Hi {{first_name}},\n\nIt's been a while since we last connected. Wanted to share a quick update — we've launched {{new_feature}} that I think could be really valuable for {{company}}.\n\nWorth a quick chat?\n\nBest,\n{{sender_name}}" },
  { id: 5, name: "Meeting Request", subject: "{{first_name}} — 15 min this week?", body: "Hi {{first_name}},\n\nI'd love to get 15 minutes on your calendar this week to discuss {{topic}}.\n\nI have availability:\n- Tuesday 2-4 PM\n- Wednesday 10 AM-12 PM\n- Thursday 1-3 PM\n\nWhat works best for you?\n\nBest,\n{{sender_name}}" },
];


const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['a','b','i','u','em','strong','p','br','div','span','ul','ol','li','blockquote','h1','h2','h3','h4','h5','h6','table','thead','tbody','tr','td','th','img','hr','pre','code'],
  ALLOWED_ATTR: ['href','src','alt','title','target','rel','style','class','width','height','align','valign','colspan','rowspan','border','cellpadding','cellspacing'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
};

// Force all rendered <a> tags to open in a new tab safely. Idempotent —
// guarded by a window-scoped flag so HMR / re-imports don't stack hooks.
if (typeof window !== 'undefined' && !window.__cf_dompurify_hooks_installed && DOMPurify && DOMPurify.addHook) {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  window.__cf_dompurify_hooks_installed = true;
}

/* ── Email Thread View ── */
function EmailView({ email, onBack, onReply, onForward, onDelete, canDelete, isMobile, bodyLoading }) {
  const { contacts: CONTACTS } = useStore();
  const ct = CONTACTS.find(c => c.id === email.ct);
  const [showQuoted, setShowQuoted] = useState(false);

  // Unquoted body (new content only) + detection of whether there's more.
  const trimmed = useMemo(() => {
    const src = email.body_text || email.body || "";
    const unquoted = stripQuotedReply(src);
    return { unquoted, hasTrimmed: unquoted.length < src.length, full: src };
  }, [email]);

  const calendarEvent = email.is_calendar_invite ? {
    title: email.calendar_event_title,
    start: email.calendar_event_start,
    end: email.calendar_event_end,
    location: email.calendar_event_location,
    organizer: email.calendar_event_organizer,
    joinUrl: email.calendar_event_join_url,
    status: email.calendar_event_status,
  } : null;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ padding: isMobile ? "10px 12px" : "12px 16px", borderBottom: "1px solid " + T.bdr, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 8 : 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <button onClick={onBack} style={{ background: "none", border: "none", color: "#FAFAFA", fontSize: 11, fontWeight: 400, marginBottom: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
            <svg width="14" height="14" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9L11 14" stroke="#FAFAFA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> Back
          </button>
          <div style={{ fontSize: isMobile ? 15 : 18, fontWeight: 400, color: T.txt }}>{email.subject}</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: T.tx2 }}>
              {email.folder === "sent" ? "To: " + email.to : "From: " + email.from}
            </span>
            <span style={{ fontSize: 10, color: T.tx3 }}>{cfDateTime(email.date)}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          <Btn onClick={onReply}>Reply</Btn>
          <Btn onClick={onForward}>Fwd</Btn>
          {canDelete && onDelete && <Btn onClick={onDelete}>Delete</Btn>}
        </div>
      </div>

      {/* Tracking info */}
      {email.folder === "sent" && (
        <div style={{ padding: "8px 16px", borderBottom: "1px solid " + T.bdr, background: T.sf }}>
          <div style={{ display: "flex", gap: 16, fontSize: 10 }}>
            <span style={{ color: T.tx3 }}>Status: <span style={{
              fontWeight: 400,
              color: "#FAFAFA",
            }}>{email.status.toUpperCase()}</span></span>
            {email.status === "opened" && <span style={{ color: T.tx3 }}>Opened 3 times, last: 2h ago</span>}
            {email.status === "replied" && <span style={{ color: "#FAFAFA" }}>Replied within 4 hours</span>}
          </div>
        </div>
      )}

      {/* Body */}
      <div style={{ flex: 1, overflow: "auto", padding: "20px 16px" }}>
        {calendarEvent && <CalendarInviteCard event={calendarEvent} />}

        {/* Lazy body_html fetch indicator. body_text is already visible
            below as a fallback — this is a subtle "fetching the rich
            version" hint, not a blocking spinner. */}
        {bodyLoading && email.body_html === undefined && (
          <div style={{ fontSize: 11, color: T.tx3, marginBottom: 10, fontStyle: "italic" }}>
            Loading full message…
          </div>
        )}

        {email.body_html ? (
          <div
            style={{ fontSize: 13, color: T.tx2, lineHeight: 1.8, maxWidth: 640 }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.body_html, SANITIZE_CONFIG) }}
          />
        ) : (
          <div style={{ fontSize: 13, color: T.tx2, lineHeight: 1.8, whiteSpace: "pre-wrap", maxWidth: 640 }}>
            {showQuoted ? trimmed.full : trimmed.unquoted}
          </div>
        )}

        {!email.body_html && trimmed.hasTrimmed && (
          <div style={{ maxWidth: 640, marginTop: 16 }}>
            <button
              onClick={() => setShowQuoted(v => !v)}
              style={{
                background: "none", border: "1px solid " + T.bdr,
                color: T.tx3, fontSize: 10, padding: "4px 10px", borderRadius: 6,
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              {showQuoted ? "Hide trimmed content" : "Show trimmed content"}
            </button>
          </div>
        )}
      </div>

      {/* Contact sidebar hint */}
      {ct && (
        <div style={{ padding: "10px 16px", borderTop: "1px solid " + T.bdr, background: T.sf }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 11, fontWeight: 400, color: T.txt }}>{ct.fn} {ct.ln}</span>
              <span style={{ fontSize: 10, color: T.tx3, marginLeft: 8 }}>{ct.co} · Score: {ct.score || "—"}</span>
            </div>
            <Badge color="rgba(217,219,221,0.12)">{ct.sent || "—"}</Badge>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════
   EMAIL PAGE
   ═══════════════════════════════════ */
export default function EmailPage({ isMobile, onSelectContact }) {
  const store = useStore();
  const { contacts: CONTACTS, logEmail, currentUserId, teamMembers: TEAM_MEMBERS, emailConnections, getAccessibleUserIds } = store;
  const auth = useAuth();
  const { currentUser } = auth;
  const toast = useToast();
  const currentOrgId = currentUser?._orgId;
  const [emails, setEmails] = useState(EMAILS_INIT);
  const [folder, setFolder] = useState("inbox");
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [composing, setComposing] = useState(false);
  const [composeTemplate, setComposeTemplate] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [forwardEmail, setForwardEmail] = useState(null);
  const [search, setSearch] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [bodyLoading, setBodyLoading] = useState(false);
  // Mobile: 'folders' | 'list' | 'detail'
  const [mobileView, setMobileView] = useState("list");

  // ── User & Team Filters ──
  const [filterUser, setFilterUser] = useState("all");
  const [filterTeam, setFilterTeam] = useState("all");
  const [teams, setTeams] = useState([]);
  const [teamMemberships, setTeamMemberships] = useState([]);

  useEffect(() => {
    const orgId = window.__coreflow_org_id;
    if (!orgId) return;
    supabase.from("teams").select("*").eq("org_id", orgId).then(({ data }) => { if (data) setTeams(data); });
    supabase.from("team_members").select("*").then(({ data }) => { if (data) setTeamMemberships(data); });
  }, []);

  // Load list-view email metadata from Supabase. body_html is intentionally
  // OMITTED from this query — it's the heaviest column (avg ~21KB) and the
  // list view only needs subject/snippet/preview. body_html is fetched on
  // demand when an email is opened (see openEmail below).
  //
  // Folder partitioning is also done client-side: this query returns ALL
  // org emails (deleted + active), and the `filtered` useMemo + folder
  // counts both filter on `deleted_at` locally. This way folder switching
  // is instant — no refetch — and the folder count badges are always
  // accurate against the full dataset, not just the visible folder.
  const LIST_COLUMNS = "id, contact_id, subject, snippet, from_address, to_address, status, direction, created_at, user_id, deleted_at, thread_id, is_calendar_invite, calendar_event_title, calendar_event_start, calendar_event_end, calendar_event_location, calendar_event_organizer, calendar_event_join_url, calendar_event_status, open_count, click_count, opened_at, replied_at, body_text, reply_to_id";
  const loadEmails = useCallback(async () => {
    try {
      const orgId = window.__coreflow_org_id;
      const { data, error } = await supabase.from("emails")
        .select(LIST_COLUMNS)
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) { console.warn("[CoreFlow Email] Load error:", error.message); return; }
      const rows = data || [];

      // Per-user read state — fetch in parallel-ish (after the email list so
      // we can scope to the visible row IDs and avoid pulling reads for
      // emails not in the current load window).
      let readsMap = new Map();
      if (currentUserId && rows.length > 0) {
        const { data: readsData, error: readsErr } = await supabase
          .from("email_reads")
          .select("email_id, read_at")
          .eq("user_id", currentUserId)
          .in("email_id", rows.map(r => r.id));
        if (readsErr) console.warn("[CoreFlow Email] reads load error:", readsErr.message);
        readsMap = new Map((readsData || []).map(r => [r.email_id, r.read_at]));
      }

      const dbEmails = rows.map(e => ({
        id: e.id,
        ct: e.contact_id || null,
        subject: e.subject || "(no subject)",
        preview: (() => {
          const src = e.snippet || e.body_text || "";
          return makePreview(src, 140);
        })(),
        date: e.created_at,
        from: e.from_address,
        to: e.to_address,
        status: e.status || "sent",
        read_at_user: readsMap.get(e.id) || null,
        read: e.direction === "outbound" || !!readsMap.get(e.id),
        starred: false,
        folder: e.deleted_at ? "trash" : (e.direction === "outbound" ? "sent" : "inbox"),
        body: e.body_text || "",
        body_html: undefined, // lazy — populated on open by openEmail
        body_text: e.body_text,
        snippet: e.snippet,
        is_calendar_invite: e.is_calendar_invite,
        calendar_event_title: e.calendar_event_title,
        calendar_event_start: e.calendar_event_start,
        calendar_event_end: e.calendar_event_end,
        calendar_event_location: e.calendar_event_location,
        calendar_event_organizer: e.calendar_event_organizer,
        calendar_event_join_url: e.calendar_event_join_url,
        calendar_event_status: e.calendar_event_status,
        direction: e.direction,
        user_id: e.user_id,
        reply_to_id: e.reply_to_id,
        thread_id: e.thread_id,
        contact_id: e.contact_id,
        open_count: e.open_count || 0,
        click_count: e.click_count || 0,
        opened_at: e.opened_at,
        replied_at: e.replied_at,
        deleted_at: e.deleted_at,
        _db: true,
      }));
      setEmails(dbEmails);
      console.log("[CoreFlow Email] Loaded", rows.length, "emails (metadata only — body_html lazy)");
    } catch (err) {
      console.warn("[CoreFlow Email] Load failed:", err.message);
    }
  }, [currentUserId]);

  useEffect(() => { loadEmails(); }, [loadEmails]);

  const handleEmailSent = () => { setTimeout(loadEmails, 1000); };

  // Open an email — lazy-fetches body_html the first time. The list view
  // doesn't carry body_html (heaviest column ~21KB), so we fetch it on
  // demand here and cache it back into the row so subsequent opens of
  // the same email don't re-fetch.
  const openEmail = useCallback(async (email) => {
    setSelected(email);
    if (isMobile) setMobileView("detail");

    // Mark as read for the current user (per-user, inbound only). Optimistic
    // local update so the row's read styling flips immediately; store helper
    // fires the DB upsert. Outbound emails are pre-marked read at load time
    // and never reach this branch.
    if (email && email._db && !email.read_at_user && email.direction === "inbound") {
      const nowIso = new Date().toISOString();
      setEmails(prev => prev.map(e => e.id === email.id ? { ...e, read_at_user: nowIso, read: true } : e));
      store.markEmailRead?.(email.id);
    }

    if (!email || !email._db) return;          // seed/local rows have no DB body
    if (email.body_html !== undefined) return; // already fetched (string or null)
    setBodyLoading(true);
    try {
      const { data, error } = await supabase.from("emails")
        .select("body_html")
        .eq("id", email.id)
        .single();
      const html = error ? null : (data?.body_html || null);
      if (error) console.warn("[CoreFlow Email] body_html fetch error:", error.message);
      setEmails(prev => prev.map(e => e.id === email.id ? { ...e, body_html: html } : e));
      setSelected(prev => prev && prev.id === email.id ? { ...prev, body_html: html } : prev);
    } finally {
      setBodyLoading(false);
    }
  }, [isMobile, store]);

  // Manual sync — fires the same cron handler that runs every 2 min, then
  // reloads the visible list. Reports new-inbound count via toast.
  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const result = await store.syncEmails();
      await loadEmails();
      const n = (result && (result.newInbound || 0)) + (result && (result.replies || 0));
      if (n > 0) toast?.success(n === 1 ? "1 new email synced" : `${n} new emails synced`);
      else toast?.success("Inbox up to date");
    } catch (e) {
      // store.syncEmails already toasts on failure; swallow here.
    } finally {
      setSyncing(false);
    }
  };

  // SMS state
  const [smsMessages, setSmsMessages] = useState([]);
  const [smsTo, setSmsTo] = useState("");
  const [smsBody, setSmsBody] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsContact, setSmsContact] = useState(null);

  const handleSendSms = async () => {
    if (!smsTo || !smsBody.trim()) return;
    setSmsSending(true);
    const ct = CONTACTS.find(c => {
      const ph = (c.ph || c.phone || "").replace(/[^\d]/g, "");
      const cleanTo = smsTo.replace(/[^\d]/g, "");
      return ph && cleanTo.endsWith(ph);
    }) || smsContact;

    try {
      const resp = await apiFetch("/api/sms/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: smsTo, body: smsBody, contactId: ct?.id || null }),
      });
      const result = await resp.json();
      if (result.success) {
        console.log("[CoreFlow] SMS sent:", result.sid);
        setSmsMessages(prev => [...prev, {
          id: result.sid, from: "You", to: smsTo, body: smsBody,
          direction: "outbound", date: new Date().toISOString(), contact: ct,
        }]);
        setSmsBody("");
      } else {
        alert("SMS failed: " + (result.error || "Unknown error"));
      }
    } catch (e) {
      console.error("[CoreFlow] SMS error:", e);
      alert("SMS failed — check console");
    } finally {
      setSmsSending(false);
    }
  };

  // Counts reflect the currently-loaded emails array, which is filtered by
  // folder at load time. The Trash count is only accurate while the user is
  // viewing Trash; in other folders it reads 0 because deleted rows aren't
  // loaded. That's an acceptable trade-off — avoids a second parallel query
  // on every folder switch for a lightly-used surface.
  // Clear bulk selection whenever the active folder changes.
  useEffect(() => { setSelectedIds(new Set()); }, [folder]);

  const handleDeleteSelected = async () => {
    if (!auth.hasPermission("emails_delete")) {
      toast?.error("You don't have permission to delete emails");
      return;
    }
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const ok = await store.softDeleteEmails(ids);
    if (!ok) return;
    setSelectedIds(new Set());
    await loadEmails();
    toast?.success(
      ids.length === 1 ? "Email moved to trash" : `${ids.length} emails moved to trash`,
      { duration: 8000, action: { label: "Undo", onClick: async () => {
        await store.restoreEmails(ids);
        await loadEmails();
      }}}
    );
  };

  const handleDeleteOne = async (id) => {
    if (!auth.hasPermission("emails_delete")) return;
    const ok = await store.softDeleteEmails([id]);
    if (!ok) return;
    setSelected(null);
    await loadEmails();
    toast?.success("Email moved to trash", {
      duration: 8000,
      action: { label: "Undo", onClick: async () => {
        await store.restoreEmails([id]);
        await loadEmails();
      }}
    });
  };

  const handleRestore = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const ok = await store.restoreEmails(ids);
    if (!ok) return;
    setSelectedIds(new Set());
    await loadEmails();
    toast?.success(ids.length === 1 ? "Email restored" : `${ids.length} emails restored`);
  };

  const handleHardDelete = async () => {
    if (!auth.hasPermission("emails_delete")) return;
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Permanently delete ${ids.length} email${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    const ok = await store.hardDeleteEmails(ids);
    if (!ok) return;
    setSelectedIds(new Set());
    await loadEmails();
    toast?.success(ids.length === 1 ? "Email permanently deleted" : `${ids.length} emails permanently deleted`);
  };

  // Folder counts derived from the FULL emails array (not the current folder),
  // so the badges always reflect the dataset. `active` excludes soft-deleted;
  // `trash` is the inverse partition.
  const counts = useMemo(() => {
    const active = emails.filter(e => !e.deleted_at);
    return {
      inbox: active.filter(e => e.direction === "inbound").length,
      sent: active.filter(e => e.direction === "outbound").length,
      opened: active.filter(e => e.status === "opened" || e.status === "replied").length,
      clicked: active.filter(e => e.status === "clicked").length,
      scheduled: active.filter(e => e.folder === "scheduled").length,
      trash: emails.filter(e => !!e.deleted_at).length,
    };
  }, [emails]);

  const folders = [
    { id: "inbox", label: "Inbox", icon: null, count: counts.inbox },
    { id: "sent", label: "Sent", icon: null, count: counts.sent },
    { id: "opened", label: "Opened", icon: null, count: counts.opened },
    { id: "clicked", label: "Clicked", icon: null, count: counts.clicked },
    { id: "scheduled", label: "Scheduled", icon: null, count: counts.scheduled },
    { id: "templates", label: "Templates", icon: null, count: TEMPLATES.length },
    { id: "trash", label: "Trash", icon: null, count: counts.trash },
  ];

  // Inbox-access boundary (UI-only). Shows emails whose user_id is the
  // current user OR is in the current user's "shared with me" set.
  // Computed once and reused by both `filtered` and `threads`.
  const accessibleIds = useMemo(
    () => (getAccessibleUserIds ? getAccessibleUserIds() : []),
    [getAccessibleUserIds]
  );
  // True when filterUser points to a user the current user has no access to.
  // Drives a dedicated empty state instead of the generic "No emails".
  const noAccessToFilteredUser = filterUser !== "all" && !accessibleIds.includes(filterUser);

  const filtered = useMemo(() => {
    let list;
    if (folder === "trash") {
      list = emails.filter(e => !!e.deleted_at);
    } else if (folder === "opened") {
      list = emails.filter(e => !e.deleted_at && (e.status === "opened" || e.status === "replied"));
    } else if (folder === "clicked") {
      list = emails.filter(e => !e.deleted_at && e.status === "clicked");
    } else {
      list = emails.filter(e => !e.deleted_at && e.folder === folder);
    }
    // User filter
    if (filterUser !== "all") {
      if (!accessibleIds.includes(filterUser)) return [];
      list = list.filter(e => e.user_id === filterUser);
    } else {
      // "All Users" still respects the inbox-access boundary
      list = list.filter(e => accessibleIds.includes(e.user_id));
    }
    // Team filter
    if (filterTeam !== "all") {
      const teamUserIds = teamMemberships.filter(tm => tm.team_id === filterTeam).map(tm => tm.user_id);
      list = list.filter(e => teamUserIds.includes(e.user_id));
    }
    if (search) list = list.filter(e => (e.subject + " " + e.preview + " " + e.from + " " + e.to).toLowerCase().includes(search.toLowerCase()));
    return list.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [emails, folder, search, filterUser, filterTeam, teamMemberships, accessibleIds]);

  // ── Build threads from emails (partitioned by trash vs active) ──
  const threads = useMemo(() => {
    // Trash and active are disjoint partitions; folder switching toggles
    // which partition is in scope. Within active, the per-folder filter
    // below still narrows further (inbox/sent/opened/clicked/scheduled).
    const sourceEmails = folder === "trash"
      ? emails.filter(e => !!e.deleted_at)
      : emails.filter(e => !e.deleted_at);
    const threadMap = {};
    // Group by normalized subject + contact email
    for (const email of sourceEmails) {
      const otherEmail = (email.direction === "outbound" ? email.to : email.from) || "";
      const normSubject = (email.subject || "").replace(/^(re|fwd):\s*/i, "").trim().toLowerCase();
      const key = otherEmail.toLowerCase() + "||" + normSubject;
      if (!threadMap[key]) threadMap[key] = { key, emails: [], lastDate: null, subject: email.subject?.replace(/^(re|fwd):\s*/i, "").trim() || "(no subject)", otherEmail, status: "sent" };
      threadMap[key].emails.push(email);
      if (!threadMap[key].lastDate || new Date(email.date) > new Date(threadMap[key].lastDate)) {
        threadMap[key].lastDate = email.date;
        threadMap[key].latestPreview = email.preview;
        threadMap[key].status = email.status;
      }
      // Thread has inbound = it has a reply
      if (email.direction === "inbound") threadMap[key].hasReply = true;
      if (email.direction === "outbound") threadMap[key].hasOutbound = true;
      // Thread inherits user_id from the first email that has one — regardless
      // of direction. Earlier this only fired on outbound, so inbound-only
      // threads (e.g., a teammate's mailbox where contacts replied) had
      // undefined t.user_id and got dropped by the user filter.
      if (email.user_id && !threadMap[key].user_id) threadMap[key].user_id = email.user_id;
      if (email.contact_id) threadMap[key].contact_id = email.contact_id;
    }

    // Sort each thread's emails chronologically
    Object.values(threadMap).forEach(t => {
      t.emails.sort((a, b) => new Date(a.date) - new Date(b.date));
      t.count = t.emails.length;
      // Thread is unread if ANY inbound email lacks a per-user read_at.
      // Outbound never counts toward unread (you wrote it).
      t.someUnread = t.emails.some(e => e.direction === "inbound" && !e.read_at_user);
      // Use the most important status
      const statuses = t.emails.map(e => e.status);
      if (statuses.includes("replied")) t.status = "replied";
      else if (statuses.includes("clicked")) t.status = "clicked";
      else if (statuses.includes("opened")) t.status = "opened";
      else if (statuses.includes("delivered")) t.status = "delivered";
      else if (statuses.includes("received")) t.status = "received";
    });

    // Filter threads same as filtered emails
    let threadList = Object.values(threadMap);
    if (folder === "inbox") threadList = threadList.filter(t => t.hasReply || t.emails.some(e => e.folder === "inbox"));
    else if (folder === "sent") threadList = threadList.filter(t => t.hasOutbound);
    else if (folder === "opened") threadList = threadList.filter(t => t.emails.some(e => e.status === "opened" || e.status === "replied"));
    else if (folder === "clicked") threadList = threadList.filter(t => t.emails.some(e => e.status === "clicked"));

    // Inbox-access boundary: same rule as `filtered`. When filterUser is a
    // specific user we lack access to, return zero threads. When filterUser
    // is "all", restrict to threads whose owner is in our accessible set.
    if (filterUser !== "all") {
      if (!accessibleIds.includes(filterUser)) return [];
      threadList = threadList.filter(t => t.user_id === filterUser);
    } else {
      threadList = threadList.filter(t => accessibleIds.includes(t.user_id));
    }
    if (filterTeam !== "all") {
      const teamUserIds = teamMemberships.filter(tm => tm.team_id === filterTeam).map(tm => tm.user_id);
      threadList = threadList.filter(t => teamUserIds.includes(t.user_id));
    }
    if (search) threadList = threadList.filter(t => (t.subject + " " + t.otherEmail + " " + t.latestPreview).toLowerCase().includes(search.toLowerCase()));

    return threadList.sort((a, b) => new Date(b.lastDate) - new Date(a.lastDate));
  }, [emails, folder, search, filterUser, filterTeam, teamMemberships, accessibleIds]);

  const stColor = { replied: "#FAFAFA", opened: "#FAFAFA", delivered: "#FAFAFA", scheduled: "#FAFAFA", clicked: "#FAFAFA" };

  // Template view
  // ── SMS View ──
  if (folder === "sms") {
    return (
      <div style={{ display: "flex", height: "100%" }}>
        {/* Sidebar — hidden on mobile */}
        {!isMobile && <div style={{ width: 200, borderRight: "1px solid rgba(217,219,221,0.10)", background: "#2C2E33", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: 12 }}>
            <Btn primary onClick={() => { setComposing(true); setReplyTo(null); setForwardEmail(null); }} style={{ width: "100%", justifyContent: "center" }}>Compose</Btn>
          </div>
          {folders.map(f => (
            <button key={f.id} onClick={() => { setFolder(f.id); setSelected(null); }} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "8px 12px", border: "none", width: "100%", textAlign: "left", cursor: "pointer",
              background: folder === f.id ? "#4059AD" : "transparent",
              borderLeft: folder === f.id ? "2px solid #4059AD" : "2px solid transparent",
              borderRadius: folder === f.id ? 6 : 0,
            }}>
              <span style={{ fontSize: 11, fontWeight: folder === f.id ? 700 : 500, color: folder === f.id ? "#FAFAFA" : T.tx2 }}>{f.label}</span>
              <span style={{ fontSize: 10, color: folder === f.id ? "#D9DBDD" : T.tx3 }}>{f.count}</span>
            </button>
          ))}
        </div>}
        {/* SMS Panel */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Contact selector */}
          <div style={{ padding: 12, borderBottom: "1px solid " + T.bdr }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: T.tx3 }}>To:</span>
              <Select value={smsTo} onChange={e => {
                const val = e.target.value;
                setSmsTo(val);
                setSmsContact(CONTACTS.find(c => (c.ph || c.phone) === val) || null);
              }} style={{ flex: 1, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 4, padding: "6px 8px", color: T.txt, fontSize: 12 }}>
                <option value="">Select a contact...</option>
                {CONTACTS.filter(c => c.ph || c.phone).map(c => (
                  <option key={c.id} value={c.ph || c.phone}>{c.fn} {c.ln} — {c.ph || c.phone}</option>
                ))}
              </Select>
              <span style={{ fontSize: 11, color: T.tx3 }}>or</span>
              <input value={smsTo} onChange={e => setSmsTo(e.target.value)}
                placeholder="+1 555 123 4567" style={{ width: 140, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 4, padding: "6px 8px", color: T.txt, fontSize: 12, outline: "none" }} />
            </div>
          </div>

          {/* Message thread */}
          <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
            {smsMessages.length === 0 ? (
              <div style={{ textAlign: "center", padding: 60, color: T.tx3 }}>
                <div style={{ fontSize: 14, fontWeight: 400, color: T.tx3 }}>SMS</div>
                <div style={{ fontSize: 13 }}>No messages yet</div>
                <div style={{ fontSize: 11, marginTop: 4 }}>Select a contact and send your first text</div>
              </div>
            ) : (
              smsMessages.filter(m => {
                if (!smsTo) return true;
                const cleanTo = smsTo.replace(/[^\d]/g, "");
                const mTo = (m.to || "").replace(/[^\d]/g, "");
                const mFrom = (m.from === "You" ? "" : (m.from || "")).replace(/[^\d]/g, "");
                return mTo.endsWith(cleanTo) || mFrom.endsWith(cleanTo) || !cleanTo;
              }).map((m, i) => (
                <div key={m.id || i} style={{
                  display: "flex", justifyContent: m.direction === "outbound" ? "flex-end" : "flex-start",
                  marginBottom: 8,
                }}>
                  <div style={{
                    maxWidth: "70%", padding: "8px 12px", borderRadius: 8,
                    background: m.direction === "outbound" ? T.blue : T.sf,
                    color: m.direction === "outbound" ? "#fff" : T.txt,
                    fontSize: 13, lineHeight: 1.5,
                  }}>
                    <div>{m.body}</div>
                    <div style={{ fontSize: 9, opacity: 0.6, marginTop: 4, textAlign: "right" }}>
                      {(() => { const _d = new Date(m.date); let _h = _d.getHours(); const _ap = _h >= 12 ? "PM" : "AM"; _h = _h % 12 || 12; return _h + ":" + String(_d.getMinutes()).padStart(2,"0") + " " + _ap; })()}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Compose SMS */}
          <div style={{ padding: 12, borderTop: "1px solid " + T.bdr, display: "flex", gap: 8, alignItems: "flex-end" }}>
            <textarea value={smsBody} onChange={e => setSmsBody(e.target.value)}
              placeholder="Type a message..." rows={2}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendSms(); } }}
              style={{
                flex: 1, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8,
                padding: "8px 12px", color: T.txt, fontSize: 13, outline: "none", resize: "none",
                lineHeight: 1.4, fontFamily: "inherit",
              }} />
            <button onClick={handleSendSms} disabled={smsSending || !smsBody.trim() || !smsTo}
              style={{
                padding: "10px 20px", borderRadius: 8, border: "none",
                background: (!smsBody.trim() || !smsTo) ? T.bdr : "#2E8B57",
                color: "#fff", fontSize: 12, fontWeight: 400, cursor: "pointer",
              }}>
              {smsSending ? "..." : "Send"}
            </button>
          </div>
          <div style={{ padding: "4px 12px 8px", fontSize: 9, color: T.tx3 }}>
            {smsBody.length}/160 characters {smsBody.length > 160 ? `(${Math.ceil(smsBody.length / 160)} segments)` : ""}
          </div>
        </div>
        {composing && <ComposeModal onClose={() => { setComposing(false); setReplyTo(null); setForwardEmail(null); setComposeTemplate(null); }} replyTo={replyTo} forwardEmail={forwardEmail} template={composeTemplate} templates={TEMPLATES} onSent={handleEmailSent} />}
      </div>
    );
  }

  if (folder === "templates") {
    return (
      <div style={{ display: "flex", height: "100%" }}>
        {/* Sidebar — hidden on mobile */}
        {!isMobile && <div style={{ width: 200, borderRight: "1px solid rgba(217,219,221,0.10)", background: "#2C2E33", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: 12 }}>
            <Btn primary onClick={() => setComposing(true)} style={{ width: "100%", justifyContent: "center" }}>Compose</Btn>
          </div>
          {folders.map(f => (
            <button key={f.id} onClick={() => { setFolder(f.id); setSelected(null); }} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "8px 12px", border: "none", width: "100%", textAlign: "left", cursor: "pointer",
              background: folder === f.id ? "#4059AD" : "transparent",
              borderLeft: folder === f.id ? "2px solid #4059AD" : "2px solid transparent",
              borderRadius: folder === f.id ? 6 : 0,
            }}>
              <span style={{ fontSize: 11, fontWeight: folder === f.id ? 700 : 500, color: folder === f.id ? "#FAFAFA" : T.tx2 }}>{f.label}</span>
              <span style={{ fontSize: 10, color: folder === f.id ? "#D9DBDD" : T.tx3 }}>{f.count}</span>
            </button>
          ))}
        </div>}
        {/* Templates list */}
        <div style={{ flex: 1, padding: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 400, color: T.txt, marginBottom: 12 }}>Email Templates</div>
          {TEMPLATES.map(t => (
            <div key={t.id} style={{ background: "#33363D", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: 14, marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 400, color: T.txt }}>{t.name}</span>
                <div style={{ display: "flex", gap: 4 }}>
                  <Btn onClick={() => { setComposeTemplate(t); setComposing(true); }} style={{ fontSize: 9 }}>Use</Btn>
                  <Btn onClick={() => { setComposeTemplate(t); setComposing(true); }} style={{ fontSize: 9 }}>Edit</Btn>
                </div>
              </div>
              <div style={{ fontSize: 11, color: T.tx2, marginBottom: 4 }}>Subject: {t.subject}</div>
              <div style={{ fontSize: 11, color: T.tx3, whiteSpace: "pre-wrap", lineHeight: 1.5, maxHeight: 60, overflow: "hidden" }}>{t.body}</div>
            </div>
          ))}
        </div>
        {composing && <ComposeModal onClose={() => { setComposing(false); setComposeTemplate(null); }} template={composeTemplate} templates={TEMPLATES} onSent={handleEmailSent} />}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", height: "100%" }}>
      {/* Folder sidebar — hidden on mobile */}
      {!isMobile && <div style={{ width: 200, borderRight: "1px solid rgba(217,219,221,0.10)", background: "#2C2E33", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: 12 }}>
          <Btn primary onClick={() => { setComposing(true); setReplyTo(null); setForwardEmail(null); }} style={{ width: "100%", justifyContent: "center" }}>Compose</Btn>
        </div>
        {folders.map(f => (
          <button key={f.id} onClick={() => { setFolder(f.id); setSelected(null); }} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "9px 14px", border: "none", width: "100%", textAlign: "left", cursor: "pointer", fontFamily: "inherit",
            background: folder === f.id ? "rgba(255,255,255,0.08)" : "transparent",
            borderLeft: folder === f.id ? "2px solid #FAFAFA" : "2px solid transparent",
          }}>
            <span style={{ fontSize: 11, fontWeight: 400, color: folder === f.id ? "#FAFAFA" : T.tx3 }}>{f.label}</span>
            <span style={{ fontSize: 10, color: folder === f.id ? "#FAFAFA" : T.tx3, opacity: 0.5 }}>{f.count}</span>
          </button>
        ))}

        {/* User & Team Filters */}
        <div style={{ padding: "8px 12px", borderTop: "1px solid rgba(217,219,221,0.06)" }}>
          <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
            style={{ width: "100%", padding: "5px 8px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "#33363D", color: "#FAFAFA", fontSize: 10, outline: "none", fontFamily: "inherit", marginBottom: 4, appearance: "none", WebkitAppearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='10' height='6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' fill='none' stroke-width='1.5'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center", paddingRight: 24 }}>
            <option value="all">All Users</option>
            {(TEAM_MEMBERS || []).map(u => {
              const userId = u._dbId || u.id;
              // "(no inbox)" suffix surfaces users who haven't connected
              // Gmail/Outlook so the filter behavior is predictable —
              // selecting them returns zero by definition.
              const hasConnection = (emailConnections || []).some(conn => conn.user_id === userId);
              const displayName = u.name || ((u.fn || "") + " " + (u.ln || "")).trim();
              return (
                <option key={userId} value={userId}>{displayName + (hasConnection ? "" : " (no inbox)")}</option>
              );
            })}
          </select>
          <select value={filterTeam} onChange={e => setFilterTeam(e.target.value)}
            style={{ width: "100%", padding: "5px 8px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "#33363D", color: "#FAFAFA", fontSize: 10, outline: "none", fontFamily: "inherit", appearance: "none", WebkitAppearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='10' height='6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' fill='none' stroke-width='1.5'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center", paddingRight: 24 }}>
            <option value="all">All Teams</option>
            {teams.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        {/* Email stats */}
        <div style={{ marginTop: "auto", padding: 12, borderTop: "1px solid " + T.bdr }}>
          <div style={{ fontSize: 9, color: T.tx3, fontWeight: 400, letterSpacing: ".08em", marginBottom: 6 }}>EMAIL STATS</div>
          {[
            { l: "Open rate", v: emails.length > 0 ? Math.round(emails.filter(e => e.status === "opened" || e.status === "replied").length / Math.max(emails.length, 1) * 100) + "%" : "—", c: "#FAFAFA" },
            { l: "Click rate", v: emails.length > 0 ? Math.round(emails.filter(e => e.status === "clicked").length / Math.max(emails.length, 1) * 100) + "%" : "—", c: "#FAFAFA" },
            { l: "Reply rate", v: emails.length > 0 ? Math.round(emails.filter(e => e.status === "replied").length / Math.max(emails.length, 1) * 100) + "%" : "—", c: "#FAFAFA" },
            { l: "Total sent", v: emails.filter(e => e.folder === "sent").length.toString(), c: "#FAFAFA" },
          ].map(s => (
            <div key={s.l} style={{ display: "flex", justifyContent: "space-between", fontSize: 10, padding: "3px 0" }}>
              <span style={{ color: T.tx3 }}>{s.l}</span>
              <span style={{ color: s.c, fontWeight: 400 }}>{s.v}</span>
            </div>
          ))}
        </div>
      </div>}

      {/* Email list or view */}
      {selected ? (
        <EmailView
          email={selected}
          onBack={() => { setSelected(null); if (isMobile) setMobileView("list"); }}
          onReply={() => { setReplyTo(selected); setForwardEmail(null); setComposing(true); }}
          onForward={() => { setForwardEmail(selected); setReplyTo(null); setComposing(true); }}
          onDelete={() => handleDeleteOne(selected.id)}
          canDelete={auth.hasPermission("emails_delete")}
          isMobile={isMobile}
          bodyLoading={bodyLoading}
        />
      ) : (
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Mobile: folder picker bar + compose button */}
          {isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: "1px solid " + T.bdr }}>
              <select value={folder} onChange={e => { setFolder(e.target.value); setSelected(null); }}
                style={{ flex: 1, padding: "7px 10px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "#33363D", color: "#FAFAFA", fontSize: 12, outline: "none", fontFamily: "inherit", appearance: "none", WebkitAppearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='10' height='6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' fill='none' stroke-width='1.5'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center", paddingRight: 24 }}>
                {folders.map(f => <option key={f.id} value={f.id}>{f.label} ({f.count})</option>)}
              </select>
              <Btn primary onClick={() => { setComposing(true); setReplyTo(null); setForwardEmail(null); }} style={{ padding: "7px 14px", fontSize: 11, flexShrink: 0 }}>Compose</Btn>
            </div>
          )}
          {/* Search bar + Sync button */}
          <div style={{ padding: "10px 16px", borderBottom: "1px solid " + T.bdr, display: "flex", alignItems: "center", gap: 8 }}>
            <style>{`.cf-email-search, .cf-email-search *, .cf-email-search input { outline: none !important; box-shadow: none !important; }`}</style>
            <div className="cf-email-search" style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, background: "#33363D", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: "5px 10px" }}>
              <span style={{ color: T.tx3, fontSize: 12 }}><Icon name="search" size={13} color={T.tx3} /></span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search emails..."
                style={{ border: "none", outline: "none", background: "transparent", color: T.txt, fontSize: 12, width: "100%", boxShadow: "none" }} />
            </div>
            <button onClick={handleSync} disabled={syncing} title="Sync now"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "#33363D", color: syncing ? T.tx3 : T.txt, fontSize: 11, cursor: syncing ? "default" : "pointer", fontFamily: "inherit", flexShrink: 0, opacity: syncing ? 0.7 : 1 }}>
              <Icon name="refresh" size={12} color={syncing ? T.tx3 : T.txt} />
              {syncing ? "Syncing..." : "Sync"}
            </button>
          </div>

          {/* Bulk action toolbar — appears when rows are selected */}
          {selectedIds.size > 0 && (
            <div style={{
              position: "sticky", top: 0, zIndex: 5,
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 16px",
              background: "rgba(64,89,173,0.12)",
              borderBottom: "1px solid " + T.bdr,
              fontSize: 11, color: T.txt,
            }}>
              <span style={{ fontWeight: 500 }}>{selectedIds.size} selected</span>
              <div style={{ flex: 1 }} />
              {folder !== "trash" && auth.hasPermission("emails_delete") && (
                <button onClick={handleDeleteSelected} style={{
                  padding: "5px 12px", borderRadius: 6, border: "1px solid rgba(229,57,53,0.35)",
                  background: "transparent", color: "#E53935", fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                }}>Delete</button>
              )}
              {folder === "trash" && auth.hasPermission("emails_delete") && (
                <>
                  <button onClick={handleRestore} style={{
                    padding: "5px 12px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.25)",
                    background: "transparent", color: T.txt, fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                  }}>Restore</button>
                  <button onClick={handleHardDelete} style={{
                    padding: "5px 12px", borderRadius: 6, border: "1px solid rgba(229,57,53,0.35)",
                    background: "rgba(229,57,53,0.08)", color: "#E53935", fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                  }}>Delete Forever</button>
                </>
              )}
              <button onClick={() => setSelectedIds(new Set())} style={{
                padding: "5px 12px", borderRadius: 6, border: "1px solid " + T.bdr,
                background: "transparent", color: T.tx3, fontSize: 11, cursor: "pointer", fontFamily: "inherit",
              }}>Clear</button>
            </div>
          )}

          {/* Email list — threaded. The wrapper key remounts the list on
              folder switch so the cfRowIn waterfall animation re-fires
              (matches ContactsList view-switch behavior — view-N key
              there, folder name here). Search keystrokes do NOT remount
              because folder hasn't changed. */}
          <div key={"email-list-" + folder} style={{ flex: 1, overflow: "auto" }}>
            {threads.length === 0 ? (
              noAccessToFilteredUser ? (() => {
                const blockedUser = (TEAM_MEMBERS || []).find(u => (u._dbId || u.id) === filterUser);
                const blockedName = blockedUser ? (blockedUser.name || ((blockedUser.fn || "") + " " + (blockedUser.ln || "")).trim()) : "this user";
                return (
                  <div style={{ textAlign: "center", padding: 40, color: T.tx3, fontSize: 12, lineHeight: 1.6, maxWidth: 380, margin: "0 auto" }}>
                    You don't have access to {blockedName}'s emails.<br />
                    Ask an admin to grant access in <span style={{ color: T.tx2 }}>Settings → Email Access</span>.
                  </div>
                );
              })() : (
                <div style={{ textAlign: "center", padding: 40, color: T.tx3, fontSize: 13 }}>No emails</div>
              )
            ) : (
              threads.map((thread, threadIdx) => {
                const ct = CONTACTS.find(c => c.id === thread.contact_id);
                const threadIds = thread.emails.map(e => e.id);
                const allInThreadSelected = threadIds.length > 0 && threadIds.every(id => selectedIds.has(id));
                // Mirror the cfRowIn schedule from src/styles/global.css that
                // tbody tr children inherit on ContactsList. Same keyframe
                // name (cfRowIn is defined globally), same easing, same delays.
                const delaySec = threadIdx < 10
                  ? 0.10 + threadIdx * 0.03
                  : threadIdx < 15
                    ? 0.39 + (threadIdx - 10) * 0.02
                    : 0.49;
                const rowAnim = {
                  animation: "cfRowIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) both",
                  animationDelay: delaySec.toFixed(2) + "s",
                };
                return (
                  <div key={thread.key} style={{ ...rowAnim, opacity: thread.someUnread ? 1 : 0.6, transition: "opacity 0.15s" }}>
                    <div onClick={() => {
                      // Open the most recent email in the thread directly.
                      // Previously this toggled an inline expansion below the
                      // row that duplicated the same email content with a
                      // Reply button — confusing UX. Now matches the inner
                      // row's prior behavior: click → full EmailView.
                      const latest = thread.emails?.[thread.emails.length - 1];
                      if (latest) openEmail(latest);
                    }} style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
                      borderBottom: "1px solid " + T.bdr, cursor: "pointer",
                      background: "transparent",
                    }}>
                      <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
                        <Checkbox
                          checked={allInThreadSelected}
                          onChange={() => {
                            setSelectedIds(prev => {
                              const next = new Set(prev);
                              if (allInThreadSelected) threadIds.forEach(id => next.delete(id));
                              else threadIds.forEach(id => next.add(id));
                              return next;
                            });
                          }}
                        />
                      </div>
                      <div style={{ flex: 1, overflow: "hidden" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                          <span style={{ display: "flex", alignItems: "center", fontSize: 12, fontWeight: thread.someUnread ? 600 : 400, color: T.txt }}>
                            {thread.someUnread && (
                              <div style={{ width: 8, height: 8, borderRadius: 4, background: "#4059AD", flexShrink: 0, marginRight: 8 }} />
                            )}
                            {ct ? ct.fn + " " + ct.ln : thread.otherEmail}
                            {thread.count > 1 && <span style={{ fontSize: 10, color: T.tx3, marginLeft: 6, fontWeight: 400 }}>({thread.count})</span>}
                          </span>
                          <span style={{ fontSize: 10, color: T.tx3 }}>{cfDateTime(thread.lastDate)}</span>
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 400, color: T.tx2, marginBottom: 2 }}>{thread.subject}</div>
                        {thread.contact_id && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onSelectContact) onSelectContact(thread.contact_id);
                            }}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              fontSize: 10, color: T.tx3, marginBottom: 2,
                              padding: "2px 6px", borderRadius: 3,
                              background: "rgba(255,255,255,0.04)", border: "1px solid " + T.bdr,
                              cursor: onSelectContact ? "pointer" : "default",
                            }}
                            onMouseEnter={(e) => { if (onSelectContact) e.currentTarget.style.color = T.txt; }}
                            onMouseLeave={(e) => { if (onSelectContact) e.currentTarget.style.color = T.tx3; }}
                          >
                            → {ct ? (ct.fn + " " + ct.ln) : "Linked contact"}
                          </div>
                        )}
                        <div style={{ fontSize: 10, color: T.tx3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{thread.latestPreview}</div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Compose modal */}
      {composing && <ComposeModal onClose={() => { setComposing(false); setReplyTo(null); setForwardEmail(null); setComposeTemplate(null); }} replyTo={replyTo} forwardEmail={forwardEmail} template={composeTemplate} templates={TEMPLATES} onSent={handleEmailSent} />}
    </div>
  );
}
